import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card } from 'react-daisyui'
import { API } from '../../utils/api'
import { Api } from '../../utils/apiServer'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { ToastContainer, toast } from 'react-toastify'
import {
    AiOutlineCalendar,
    AiOutlineShoppingCart,
    AiOutlineCreditCard,
    AiOutlineFileText,
    AiOutlineCheckCircle,
    AiOutlineClockCircle,
    AiOutlineClose,
    AiOutlineCloseCircle,
    AiOutlineCopy,
    AiFillStar,
    AiOutlineStar,
} from "react-icons/ai"
import MidT from '../../components/MidT'
import DefaultProduct from "../../assets/default-product.jpg"
import { isMidtransGateway, isSafePaymentRedirectUrl, isXenditGateway, paymentGatewayPresentValue, paymentGatewayRedirectUrl } from '../../utils/paymentGateway'
import QrisPaymentModal from '../../components/QrisPaymentModal'

const ROWS_PER_PAGE = 10

const toSafeInteger = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? Math.floor(parsed) : fallback
}

const toSafeNumber = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
}

const normalizeStatus = (status) => `${status || ''}`.toLowerCase()

const normalizeReviewStatus = (status) => `${status || ''}`
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')

const FINAL_REVIEW_APPROVAL_STATUSES = [
    'completed',
    'complete',
]

const SHIPPING_STATUS_NAME_BY_ID = {
    1: 'Dikemas',
    2: 'Mencari kurir',
    3: 'Diterima kurir',
    4: 'Menuju pickup',
    5: 'Barang diambil',
    6: 'Dikirim kurir',
    7: 'Diterima',
    8: 'Ditolak',
    9: 'Pickup gagal',
    10: 'On Hold',
    11: 'Cancelled',
    12: 'No Driver Found',
}

const PROVIDER_SHIPPING_STATUS_LABELS = {
    finding_driver: 'Mencari kurir',
    confirmed: 'Mencari kurir',
    driver_allocated: 'Diterima kurir',
    allocated: 'Diterima kurir',
    enroute_pickup: 'Menuju pickup',
    out_for_pickup: 'Menuju pickup',
    picked: 'Barang diambil',
    enroute_drop: 'Dikirim kurir',
    out_for_delivery: 'Dikirim kurir',
    completed: 'Diterima',
    delivered: 'Diterima',
    rejected: 'Ditolak',
    pickup_failed: 'Pickup gagal',
    on_hold: 'On Hold',
    cancelled: 'Cancelled',
    driver_not_found: 'No Driver Found',
    no_driver: 'No Driver Found',
    dnf: 'No Driver Found',
}

const normalizeIdentifier = (value) => {
    if (value === null || value === undefined) return ''
    return `${value}`.trim()
}

const parseDetailPayload = (payload) => {
    if (!payload) return {}
    if (typeof payload === 'object') return payload
    if (typeof payload !== 'string') return {}

    try {
        return JSON.parse(payload)
    } catch (error) {
        return {}
    }
}

const getTransactionDetailList = (transactionRow) => (
    Array.isArray(transactionRow?.transaction_detail)
        ? transactionRow.transaction_detail
        : []
)

const pickFirstValue = (candidates) => {
    for (const value of candidates) {
        if (value === null || value === undefined) continue
        if (typeof value === 'string' && value.trim() === '') continue
        return value
    }
    return null
}

// A retail transaction stores split-payment allocation in transaction_data.
// Use the gateway allocation when resuming QRIS, because transaction_total_amount
// still represents the complete order total before the loyalty balance is used.
const getQrisGatewayAmount = (transactionRow) => {
    const transactionData = parseDetailPayload(transactionRow?.transaction_data)
    const walletPayment = parseDetailPayload(transactionData?.wallet_payment)
    const allocation = parseDetailPayload(transactionRow?.payment_allocation || transactionData?.payment_allocation)
    const amount = pickFirstValue([
        transactionRow?.gateway_charge_amount,
        transactionRow?.qris_amount,
        allocation?.gateway_charge_amount,
        allocation?.qris_amount,
        walletPayment?.gateway_charge_amount,
        walletPayment?.qris_amount,
    ])
    const parsed = Number(amount)

    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

const getWalletBalanceAmount = (transactionRow) => {
    const transactionData = parseDetailPayload(transactionRow?.transaction_data)
    const walletPayment = parseDetailPayload(transactionData?.wallet_payment)
    const allocation = parseDetailPayload(transactionRow?.payment_allocation || transactionData?.payment_allocation)
    const amount = pickFirstValue([
        transactionRow?.loyalty_balance_amount,
        transactionRow?.wallet_amount,
        allocation?.loyalty_balance_amount,
        allocation?.wallet_amount,
        walletPayment?.loyalty_balance_amount,
        walletPayment?.wallet_amount,
    ])
    const parsed = Number(amount)

    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
}

const getDetailItemId = (detail) => {
    const payload = parseDetailPayload(detail?.transaction_detail_payload)
    return pickFirstValue([
        detail?.item_id,
        detail?.item?.item_id,
        detail?.product?.item_id,
        payload?.item_id,
        payload?.item?.item_id,
        payload?.product?.item_id,
        payload?.menu?.item_id,
        payload?.menu?.item?.item_id,
    ])
}

const getDetailItemName = (detail) => {
    const payload = parseDetailPayload(detail?.transaction_detail_payload)
    return `${pickFirstValue([
        detail?.transaction_detail_item_name,
        detail?.item_name,
        detail?.item?.item_name,
        detail?.product?.item_name,
        payload?.item_name,
        payload?.item?.item_name,
        payload?.product?.item_name,
        payload?.menu?.item_name,
        payload?.menu?.item?.item_name,
    ]) || 'Produk tidak diketahui'}`.trim()
}

const getDetailItemImage = (detail) => {
    const payload = parseDetailPayload(detail?.transaction_detail_payload)
    const image = pickFirstValue([
        Array.isArray(payload?.item_image) ? payload.item_image[0] : payload?.item_image,
        Array.isArray(payload?.item?.item_image) ? payload.item.item_image[0] : payload?.item?.item_image,
        Array.isArray(payload?.product?.item_image) ? payload.product.item_image[0] : payload?.product?.item_image,
        detail?.item_image,
    ])

    return image || DefaultProduct
}

const getDetailReviewEligibility = (detail) => (
    detail?.review_eligibility ||
    detail?.reviewEligibility ||
    detail?.review ||
    null
)

const hasFinalReviewStatus = (candidates, finalStatuses) => (
    candidates.some((candidate) => {
        const normalized = normalizeReviewStatus(candidate)
        if (!normalized) return false
        if (finalStatuses.includes(normalized)) return true
        return finalStatuses.some((status) => normalized.includes(status))
    })
)

const isTransactionEligibleForReview = (transactionRow) => {
    const approvalCandidates = [
        transactionRow?.transaction_approve_status_name,
        transactionRow?.transaction_payment_status_name,
        transactionRow?.transaction_status_name,
        transactionRow?.payment_status,
        transactionRow?.approve_status,
        transactionRow?.status,
        transactionRow?.ms_transaction_status?.ms_transaction_status_name,
        transactionRow?.ms_payment_status?.ms_payment_status_name,
        transactionRow?.ms_approve_status?.ms_approve_status_name,
    ]
    return hasFinalReviewStatus(approvalCandidates, FINAL_REVIEW_APPROVAL_STATUSES)
}

const getDetailAlreadyReviewed = (detail) => {
    const eligibility = getDetailReviewEligibility(detail)
    if (eligibility && eligibility.already_reviewed !== undefined) {
        return Boolean(eligibility.already_reviewed)
    }
    if (eligibility && eligibility.alreadyReviewed !== undefined) {
        return Boolean(eligibility.alreadyReviewed)
    }
    return Boolean(detail?.already_reviewed || detail?.alreadyReviewed || detail?.reviewed)
}

const getDetailCanReview = (transactionRow, detail) => {
    const eligibility = getDetailReviewEligibility(detail)
    if (eligibility && eligibility.can_review !== undefined) {
        return Boolean(eligibility.can_review)
    }
    if (eligibility && eligibility.canReview !== undefined) {
        return Boolean(eligibility.canReview)
    }

    return Boolean(
        getDetailItemId(detail) &&
        isTransactionEligibleForReview(transactionRow) &&
        !getDetailAlreadyReviewed(detail)
    )
}

const getReviewReason = (transactionRow, detail) => {
    const eligibility = getDetailReviewEligibility(detail)
    if (eligibility?.reason) return eligibility.reason
    if (!getDetailItemId(detail)) return 'item_id tidak tersedia'
    if (getDetailAlreadyReviewed(detail)) return 'Sudah direview'
    if (!isTransactionEligibleForReview(transactionRow)) return 'Transaksi belum selesai'
    return null
}

const getTransactionReference = (transactionRow) => ({
    transaction_id: pickFirstValue([
        transactionRow?.transaction_id,
        transactionRow?.id,
    ]),
    transaction_number: pickFirstValue([
        transactionRow?.transaction_number,
        transactionRow?.transaction_no,
        transactionRow?.number,
    ]),
})

const getTransactionStatusMeta = (status) => {
    const normalized = normalizeStatus(status)

    if (normalized === 'approved' || normalized === 'success' || normalized === 'completed' || normalized === 'complete') {
        return {
            icon: <AiOutlineCheckCircle className="text-green-600" />,
            className: 'border border-green-200 bg-green-50 text-green-700',
        }
    }

    if (normalized === 'pending' || normalized === 'diproses' || normalized === 'processing' || normalized === 'progressing' || normalized === 'verified') {
        return {
            icon: <AiOutlineClockCircle className="text-amber-600" />,
            className: 'border border-amber-200 bg-amber-50 text-amber-700',
        }
    }

    if (normalized === 'rejected' || normalized === 'failed') {
        return {
            icon: <AiOutlineCloseCircle className="text-red-600" />,
            className: 'border border-red-100 bg-red-50 text-red-600',
        }
    }

    return {
        icon: <AiOutlineFileText className="text-gray-500" />,
        className: 'border border-gray-200 bg-gray-50 text-gray-600',
    }
}

const normalizeShippingStatusKey = (value) => `${value || ''}`
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, '_')

const getShippingStatusNameById = (statusId) => {
    const parsed = Number(statusId)
    if (!Number.isFinite(parsed)) return null
    return SHIPPING_STATUS_NAME_BY_ID[Math.floor(parsed)] || null
}

const formatShippingStatusName = (status) => {
    const value = `${status || ''}`.trim()
    if (!value) return null

    const mappedById = getShippingStatusNameById(value)
    if (mappedById) return mappedById

    return PROVIDER_SHIPPING_STATUS_LABELS[normalizeShippingStatusKey(value)] || value
}

const getShippingStatusName = (transactionRow) => {
    const transactionPayload = parseDetailPayload(
        transactionRow?.transaction_payload ||
        transactionRow?.payload ||
        transactionRow?.request ||
        transactionRow?.transaction_request
    )
    const shippingDetail = parseDetailPayload(
        transactionRow?.shipping_service_detail ||
        transactionPayload?.shipping_service_detail
    )
    const booking = shippingDetail?.booking || {}
    const bookingResponse = booking?.response || {}

    return formatShippingStatusName(pickFirstValue([
        transactionRow?.ms_shipping_status?.ms_shipping_status_name,
        transactionRow?.transaction_shipping_status_name,
        transactionRow?.shipping_status_name,
        transactionRow?.delivery_status_name,
        transactionRow?.transaction_delivery_status_name,
        transactionRow?.ms_delivery_status?.ms_delivery_status_name,
        transactionRow?.ms_delivery_status_name,
        transactionRow?.shipping_status_label,
        transactionRow?.shipping_status_text,
        transactionRow?.shipping_status,
        transactionRow?.delivery_status,
        shippingDetail?.shipping_status_name,
        shippingDetail?.delivery_status_name,
        shippingDetail?.status_name,
        shippingDetail?.status,
        shippingDetail?.booking_status,
        shippingDetail?.provider_status,
        bookingResponse?.status,
        bookingResponse?.booking_status,
        bookingResponse?.bookingStatus,
        getShippingStatusNameById(pickFirstValue([
            transactionRow?.ms_shipping_status_id,
            transactionRow?.shipping_status_id,
            transactionRow?.delivery_status_id,
            shippingDetail?.ms_shipping_status_id,
            shippingDetail?.shipping_status_id,
        ])),
    ])) || ''
}

const getCustomerReceiveConfirmationStatus = (transactionRow) => {
    const transactionData = parseDetailPayload(transactionRow?.transaction_data)
    const confirmationData = parseDetailPayload(transactionData?.retail_courier_confirmation)

    return pickFirstValue([
        transactionRow?.customer_receive_confirmation_status,
        transactionRow?.customerReceiveConfirmationStatus,
        confirmationData?.status,
    ])
}

const getCustomerReceiveConfirmedAt = (transactionRow) => {
    const transactionData = parseDetailPayload(transactionRow?.transaction_data)
    const confirmationData = parseDetailPayload(transactionData?.retail_courier_confirmation)

    return pickFirstValue([
        transactionRow?.customer_receive_confirmed_datetime,
        transactionRow?.customerReceiveConfirmedDatetime,
        confirmationData?.completed_at,
    ])
}

const getCustomerReceiveComplainedAt = (transactionRow) => {
    const transactionData = parseDetailPayload(transactionRow?.transaction_data)
    const confirmationData = parseDetailPayload(transactionData?.retail_courier_confirmation)

    return pickFirstValue([
        transactionRow?.customer_receive_complained_datetime,
        transactionRow?.customerReceiveComplainedDatetime,
        confirmationData?.complained_at,
    ])
}

const isShippingReceivable = (transactionRow) => {
    const confirmationStatus = normalizeReviewStatus(getCustomerReceiveConfirmationStatus(transactionRow))
    if (getCustomerReceiveConfirmedAt(transactionRow)) return false
    if (['completed', 'complete', 'auto completed'].includes(confirmationStatus)) return false
    if (confirmationStatus === 'complained' || getCustomerReceiveComplainedAt(transactionRow)) return true
    if (confirmationStatus === 'waiting') return true

    const approvalStatus = normalizeReviewStatus(transactionRow?.transaction_approve_status_name || transactionRow?.status)
    const normalizedStatus = normalizeShippingStatusKey(getShippingStatusName(transactionRow))
    const shippingStatusId = Number(transactionRow?.ms_shipping_status_id || transactionRow?.shipping_status_id || transactionRow?.delivery_status_id)

    return (
        ['verified', 'progressing'].includes(approvalStatus) &&
        (shippingStatusId === 7 || ['diterima', 'received', 'delivered', 'completed', 'complete'].includes(normalizedStatus))
    )
}

const getTransactionKey = (transactionRow, index) => {
    const transactionId = transactionRow?.transaction_id || ''
    const transactionNumber = transactionRow?.transaction_number || ''
    return `${transactionId}-${transactionNumber}-${index}`
}

const formatShortTransactionId = (transactionNumber) => {
    const fullId = `${transactionNumber || ''}`.trim()
    if (!fullId) return '-'

    const compact = fullId.replace(/-/g, '')
    const target = compact.length > 0 ? compact : fullId
    return `#${target.slice(-8)}`
}

const getTransactionItemNames = (transactionRow) => {
    const detailList = getTransactionDetailList(transactionRow)

    const rawNames = detailList
        .map((detail) => getDetailItemName(detail))
        .filter((name) => name.length > 0)

    if (rawNames.length === 0) return ['Produk tidak diketahui']

    if (rawNames.length === 1 && rawNames[0].includes(' - ')) {
        const splitNames = rawNames[0]
            .split(' - ')
            .map((name) => name.trim())
            .filter((name) => name.length > 0)

        if (splitNames.length > 0) return splitNames
    }

    return rawNames
}

const appendUniqueTransactions = (currentList, incomingList) => {
    if (currentList.length === 0) return incomingList
    if (incomingList.length === 0) return currentList

    const known = new Set(
        currentList.map((entry) => `${entry?.transaction_id || ''}-${entry?.transaction_number || ''}`)
    )

    const next = [...currentList]
    for (const entry of incomingList) {
        const key = `${entry?.transaction_id || ''}-${entry?.transaction_number || ''}`
        if (!known.has(key)) {
            known.add(key)
            next.push(entry)
        }
    }

    return next
}

const fallbackCopyText = (text) => {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(textarea)
    return copied
}

const normalizeWhatsappNumber = (value) => {
    let digits = `${value || ''}`.replace(/\D/g, '')
    if (!digits) return null
    if (digits.startsWith('0')) digits = `62${digits.slice(1)}`
    if (digits.startsWith('8')) digits = `62${digits}`
    return /^62\d{8,14}$/.test(digits) ? digits : null
}

const getStoreWhatsappNumber = (transactionRow) => normalizeWhatsappNumber(pickFirstValue([
    transactionRow?.store?.store_phone,
    transactionRow?.store?.store_whatsapp,
    transactionRow?.store_phone,
    transactionRow?.seller_store_phone,
]))

function TransactionList() {
    const [page, setPage] = useState(1)
    const [rows] = useState(ROWS_PER_PAGE)
    const [transaction, setTransaction] = useState([])
    const [lastPage, setLastPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [token, setToken] = useState()
    const [paymentGateway, setPaymentGateway] = useState(null)
    const [qrisPayment, setQrisPayment] = useState(null)
	const [afterPaymentSchedule, setAfterPaymentSchedule] = useState(null)
	const [afterPaymentScheduledAt, setAfterPaymentScheduledAt] = useState('')
	const [afterPaymentScheduleSaving, setAfterPaymentScheduleSaving] = useState(false)
    const [expandedTransactions, setExpandedTransactions] = useState({})
    const [reviewModal, setReviewModal] = useState({
        open: false,
        transaction: null,
        detail: null,
    })
    const [reviewRating, setReviewRating] = useState(0)
    const [reviewComment, setReviewComment] = useState('')
    const [reviewSubmitting, setReviewSubmitting] = useState(false)
    const [receivingTransactionKey, setReceivingTransactionKey] = useState(null)

    const navigate = useNavigate()

    useEffect(() => {
        const snapScript = process.env.REACT_APP_MIDTRANS_SNAP_SCRIPT
        if (!snapScript) return

        const existingScript = document.querySelector(`script[src="${snapScript}"]`)
        if (existingScript) return

        const scriptTag = document.createElement('script')
        scriptTag.src = snapScript
        scriptTag.async = true
        document.body.appendChild(scriptTag)
    }, [])

    const loadTransactions = useCallback(async (targetPage = 1, append = false) => {
        if (append) {
            setLoadingMore(true)
        } else {
            setLoading(true)
        }

        const params = {
            access: 'auth',
            url: API.TRANSACTION_LIST.url,
            method: API.TRANSACTION_LIST.type,
            reqBody: {
                page: targetPage,
                rows: rows,
                order_by: "transaction_created_datetime",
                sort_by: "desc"
            }
        }
        try {
            const response = await Api(params)
            if (response.status) {
                const responseData = response?.data || {}
                const nextPage = toSafeInteger(responseData?.page, targetPage)
                const nextLastPage = toSafeInteger(responseData?.last_page, nextPage)
                const incomingTransactions = Array.isArray(responseData?.data) ? responseData.data : []

                setTransaction((prev) => (
                    append
                        ? appendUniqueTransactions(prev, incomingTransactions)
                        : incomingTransactions
                ))
                setPage(nextPage)
                setLastPage(nextLastPage)
            } else {
                if (response?.code === 'XENDIT_PAYMENT_EXPIRED') {
                    toast.info(response.message || 'Waktu pembayaran telah habis. Status transaksi diperbarui.')
                    await loadTransactions(1, false)
                    return
                }
                toast.error(response.message)
            }
        } catch (error) {
            toast.error('Terjadi kesalahan saat memuat data')
        } finally {
            if (append) {
                setLoadingMore(false)
            } else {
                setLoading(false)
            }
        }
    }, [rows])

    useEffect(() => {
        loadTransactions(1, false)
    }, [loadTransactions])

    const handleLoadMore = async () => {
        const hasMore = page < lastPage
        if (!hasMore || loadingMore) return

        await loadTransactions(page + 1, true)
    }

    const fetchQrisStatus = useCallback(async () => {
        const current = qrisPayment?.transaction || {}
        const reference = getTransactionReference(current)
        if (!reference.transaction_id && !reference.transaction_number) return null
        const response = await Api({
            access: 'auth',
            url: API.TRANSACTION_GET.url,
            method: API.TRANSACTION_GET.type,
            reqBody: reference
        })
        return response?.status ? response?.data?.payment_gateway || null : null
    }, [qrisPayment])

    const closeQrisPayment = useCallback(() => setQrisPayment(null), [])
    const handleQrisExpired = useCallback(() => {
        loadTransactions(1, false)
    }, [loadTransactions])
	const toLocalDatetimeInput = (value) => {
		const date = new Date(value)
		return Number.isNaN(date.getTime()) ? '' : new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)
	}
	const handleQrisSuccess = useCallback(async () => {
		const current = qrisPayment?.transaction || {}
		const reference = getTransactionReference(current)
		const response = await Api({ access: 'auth', url: API.TRANSACTION_GET.url, method: API.TRANSACTION_GET.type, reqBody: reference })
		const schedule = response?.status ? response?.data?.delivery_schedule : null
		if (schedule?.required_after_payment && !schedule?.scheduled_at) {
			setQrisPayment(null)
			setAfterPaymentSchedule({ transaction_id: reference.transaction_id, schedule })
			setAfterPaymentScheduledAt(toLocalDatetimeInput(schedule.earliest_schedule_at))
			return
		}
		setQrisPayment(null)
		loadTransactions(1, false)
		navigate('/transaction/list', { replace: true })
	}, [loadTransactions, navigate, qrisPayment])

	const saveAfterPaymentSchedule = async () => {
		const schedule = afterPaymentSchedule?.schedule || {}
		const selected = new Date(afterPaymentScheduledAt)
		const earliest = new Date(schedule.earliest_schedule_at)
		const latest = new Date(schedule.latest_schedule_at)
		if (Number.isNaN(selected.getTime()) || selected < earliest || selected > latest) return toast.error('Pilih jam yang berada dalam jam operasional toko')
		setAfterPaymentScheduleSaving(true)
		try {
			const result = await Api({ access: 'auth', url: API.TRANSACTION_DELIVERY_SCHEDULE.url, method: API.TRANSACTION_DELIVERY_SCHEDULE.type, reqBody: { transaction_id: afterPaymentSchedule.transaction_id, delivery_scheduled_at: selected.toISOString() } })
			if (!result?.status) return toast.error(result?.message || 'Gagal menyimpan jadwal pengiriman')
			setAfterPaymentSchedule(null)
			await loadTransactions(1, false)
		} finally { setAfterPaymentScheduleSaving(false) }
	}

    const handleToggleExpanded = (transactionKey) => {
        setExpandedTransactions((prev) => ({
            ...prev,
            [transactionKey]: !prev[transactionKey],
        }))
    }

    const getTransactionDetailIdentifier = (transactionRow) => {
        const reference = getTransactionReference(transactionRow)
        return reference.transaction_number || reference.transaction_id || ''
    }

    const handleOpenTransactionDetail = (transactionRow) => {
        const identifier = getTransactionDetailIdentifier(transactionRow)
        if (!identifier) {
            toast.error('ID transaksi tidak tersedia')
            return
        }

        navigate(`/transaction/detail/${encodeURIComponent(identifier)}`, {
            state: {
                transaction: transactionRow,
            },
        })
    }

    const handleCopyTransactionId = async (event, transactionNumber) => {
        event.stopPropagation()
        const fullId = `${transactionNumber || ''}`.trim()
        if (!fullId) {
            toast.error('ID transaksi tidak tersedia')
            return
        }

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(fullId)
            } else {
                const copied = fallbackCopyText(fullId)
                if (!copied) throw new Error('copy-failed')
            }
            toast.success('ID transaksi berhasil disalin')
        } catch (error) {
            toast.error('Gagal menyalin ID transaksi')
        }
    }

    const handlePaymentHelp = async (event, transactionRow) => {
        event.stopPropagation()
        const reference = getTransactionReference(transactionRow)
        const transactionId = reference.transaction_id || reference.transaction_number
        if (!transactionId) {
            toast.error('ID transaksi tidak tersedia')
            return
        }

        let phone = getStoreWhatsappNumber(transactionRow)
        // The list endpoint can omit store contact data. Open a same-origin
        // placeholder during the click event so the eventual WhatsApp URL is
        // not blocked as a popup after fetching the transaction detail.
        const helpWindow = phone ? null : window.open('about:blank', '_blank')
        if (helpWindow) helpWindow.opener = null

        if (!phone) {
            try {
                const response = await Api({
                    access: 'auth',
                    url: API.TRANSACTION_GET.url,
                    method: API.TRANSACTION_GET.type,
                    reqBody: reference
                })
                if (!response?.status) throw new Error(response?.message || 'Gagal memuat data toko')
                phone = getStoreWhatsappNumber(response?.data || {})
            } catch (error) {
                if (helpWindow && !helpWindow.closed) helpWindow.close()
                toast.error(error?.message || 'Gagal memuat nomor WhatsApp toko')
                return
            }
        }

        if (!phone) {
            if (helpWindow && !helpWindow.closed) helpWindow.close()
            toast.error('Nomor WhatsApp toko tidak tersedia')
            return
        }

        const message = `Hallo kak, pembayaran transaksi saya nomor #${transactionId} mengalami kendala, mohon dibantu`
        const whatsappUrl = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
        if (helpWindow && !helpWindow.closed) helpWindow.location.assign(whatsappUrl)
        else window.open(whatsappUrl, '_blank', 'noopener,noreferrer')
    }

    const handleResume = async (data) => {
        setToken(undefined)
        setQrisPayment(null)
        const params = {
            access: 'auth',
            url: API.TRANSACTION_RESUME.url,
            method: API.TRANSACTION_RESUME.type,
            reqBody: {
                transaction_id: data.transaction_id
            }
        }
        try {
            const response = await Api(params)
            if (response.status) {
                const gateway = response?.data?.payment_gateway || null
                setPaymentGateway(gateway)

                if (isMidtransGateway(gateway) || (!gateway && response?.data?.token)) {
                    setToken(response.data.token)
                    toast.success('Melanjutkan pembayaran...')
                    return
                }

                if (isXenditGateway(gateway)) {
                    const qrString = paymentGatewayPresentValue(gateway, 'QR_STRING')
                    if (qrString) {
                        const transaction = response?.data?.data || data
                        const amount = getQrisGatewayAmount(transaction) ?? getQrisGatewayAmount(data)
                        setQrisPayment({ value: qrString, gateway, transaction, amount })
                        return
                    }

                    const redirectUrl = paymentGatewayRedirectUrl(gateway)
                    if (!isSafePaymentRedirectUrl(redirectUrl)) {
                        toast.error('Instruksi pembayaran XENDIT tidak tersedia atau tidak valid')
                        return
                    }
                    window.location.assign(redirectUrl)
                    return
                }

                toast.error('Instruksi pembayaran tidak tersedia')
            } else {
                toast.error(response.message)
            }
        } catch (error) {
            toast.error('Terjadi kesalahan saat memproses pembayaran')
        }
    }

    const handleReceiveShipping = async (event, transactionRow, transactionKey) => {
        event.stopPropagation()

        const reference = getTransactionReference(transactionRow)
        if (!reference.transaction_number) {
            toast.error('Nomor transaksi tidak tersedia')
            return
        }

        const confirmed = window.confirm('Tandai pesanan ini selesai?')
        if (!confirmed) return

        setReceivingTransactionKey(transactionKey)

        const params = {
            access: 'auth',
            url: API.TRANSACTION_COMPLETE.url,
            method: API.TRANSACTION_COMPLETE.type,
            reqBody: {
                transaction_number: reference.transaction_number,
            },
        }

        try {
            const response = await Api(params)
            if (response?.status) {
                toast.success(response.message || 'Transaksi berhasil diselesaikan')
                await loadTransactions(1, false)
            } else {
                toast.error(response?.message || response?.error || 'Gagal menyelesaikan transaksi')
            }
        } catch (error) {
            toast.error('Terjadi kesalahan saat menyelesaikan transaksi')
        } finally {
            setReceivingTransactionKey(null)
        }
    }

    const closeReviewModal = () => {
        if (reviewSubmitting) return
        setReviewModal({
            open: false,
            transaction: null,
            detail: null,
        })
        setReviewRating(0)
        setReviewComment('')
    }

    const handleOpenReviewModal = (event, transactionRow, detail) => {
        event.stopPropagation()
        if (!getDetailCanReview(transactionRow, detail)) {
            const reason = getReviewReason(transactionRow, detail)
            if (reason) toast.error(reason)
            return
        }

        setReviewModal({
            open: true,
            transaction: transactionRow,
            detail,
        })
        setReviewRating(0)
        setReviewComment('')
    }

    const markTransactionItemReviewed = (transactionRow, itemId) => {
        const targetReference = getTransactionReference(transactionRow)
        const targetTransactionId = normalizeIdentifier(targetReference.transaction_id)
        const targetTransactionNumber = normalizeIdentifier(targetReference.transaction_number)
        const targetItemId = normalizeIdentifier(itemId)

        setTransaction((currentTransactions) => currentTransactions.map((row) => {
            const rowReference = getTransactionReference(row)
            const sameTransaction = Boolean(
                (targetTransactionId && normalizeIdentifier(rowReference.transaction_id) === targetTransactionId) ||
                (targetTransactionNumber && normalizeIdentifier(rowReference.transaction_number) === targetTransactionNumber)
            )

            if (!sameTransaction) return row

            return {
                ...row,
                transaction_detail: getTransactionDetailList(row).map((detail) => {
                    if (normalizeIdentifier(getDetailItemId(detail)) !== targetItemId) return detail

                    const currentEligibility = getDetailReviewEligibility(detail) || {}
                    return {
                        ...detail,
                        review_eligibility: {
                            ...currentEligibility,
                            item_id: itemId,
                            can_review: false,
                            already_reviewed: true,
                            reason: 'Sudah direview',
                        },
                    }
                }),
            }
        }))
    }

    const handleSubmitReview = async (event) => {
        event.preventDefault()
        if (!reviewModal.transaction || !reviewModal.detail) return

        if (reviewRating < 1 || reviewRating > 5) {
            toast.error('Pilih rating 1 sampai 5')
            return
        }

        const itemId = getDetailItemId(reviewModal.detail)
        const transactionReference = getTransactionReference(reviewModal.transaction)

        if (!itemId) {
            toast.error('item_id tidak tersedia')
            return
        }

        setReviewSubmitting(true)
        const params = {
            access: 'auth',
            url: API.MEMBER_PRODUCT_REVIEW.url,
            method: API.MEMBER_PRODUCT_REVIEW.type,
            reqBody: {
                transaction_id: transactionReference.transaction_id,
                transaction_number: transactionReference.transaction_number,
                item_id: itemId,
                rating: reviewRating,
                comment: reviewComment.trim(),
            },
        }

        try {
            const response = await Api(params)
            if (response?.status) {
                toast.success(response.message || 'Review berhasil dikirim')
                markTransactionItemReviewed(reviewModal.transaction, itemId)
                setReviewModal({
                    open: false,
                    transaction: null,
                    detail: null,
                })
                setReviewRating(0)
                setReviewComment('')
            } else {
                toast.error(response?.message || 'Review gagal dikirim')
            }
        } catch (error) {
            toast.error('Terjadi kesalahan saat mengirim review')
        } finally {
            setReviewSubmitting(false)
        }
    }

    const renderReviewAction = (transactionRow, detail) => {
        if (!detail) return null

        if (getDetailAlreadyReviewed(detail)) {
            return (
                <span className="inline-flex items-center justify-center rounded-md border border-green-200 bg-green-50 px-2.5 py-1 text-[11px] font-semibold text-green-700">
                    Sudah direview
                </span>
            )
        }

        if (!getDetailCanReview(transactionRow, detail)) return null

        return (
            <button
                type="button"
                className="inline-flex items-center justify-center rounded-md border border-yellow-200 bg-yellow-50 px-3 py-1 text-[11px] font-semibold text-yellow-700 hover:bg-yellow-100"
                onClick={(event) => handleOpenReviewModal(event, transactionRow, detail)}
            >
                Review
            </button>
        )
    }

    // Loading skeleton component
    const LoadingSkeleton = () => (
        <div className="animate-pulse space-y-4">
            <div className="bg-gray-200 h-8 rounded w-1/3 mx-auto"></div>
            <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                    <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
                ))}
            </div>
        </div>
    )

    const handlePaySuccess = (result) => {
        navigate(`/order/status/success`, { replace: true })
    }

    const handlePayError = (result) => {
        navigate(`/order/status/error`, { replace: true })
    }

    const handlePayPending = (result) => {
        navigate(`/order/status/pending`)
    }

    const handlePayClose = (result) => {
        setToken(null)
        navigate(`/order/status/nopay`)
    }

    const hasMore = page < lastPage
    const monthTransactionCount = useMemo(() => {
        const now = moment()
        return transaction.filter((row) => {
            const createdDate = moment(row?.transaction_created_datetime)
            return createdDate.isValid() && createdDate.isSame(now, 'month')
        }).length
    }, [transaction])
    const reviewProductName = reviewModal.detail ? getDetailItemName(reviewModal.detail) : ''
    const reviewProductImage = reviewModal.detail ? getDetailItemImage(reviewModal.detail) : DefaultProduct
    const reviewTransactionReference = reviewModal.transaction
        ? getTransactionReference(reviewModal.transaction)
        : {}

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="mx-auto max-w-sm px-5 py-6">
                    <LoadingSkeleton />
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <ToastContainer autoClose={1000} />
            <div className="mx-auto max-w-sm px-5 py-6">
                <div className='flex flex-col space-y-6 scrollbar-hide'>

                    {/* Header Section */}
                    <div className="rounded-lg bg-white px-4 py-3 shadow-sm border border-gray-100">
                        <p className="text-sm font-medium text-gray-700">
                            Bulan Ini ({monthTransactionCount} Transaksi)
                        </p>
                    </div>

                    {/* Transaction List */}
                    {transaction.length === 0 ? (
                        <Card className="bg-gradient-to-r from-gray-50 to-white border-2 border-dashed border-gray-300">
                            <Card.Body className="text-center py-12">
                                <AiOutlineCreditCard className="text-6xl text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 font-medium text-lg">Belum ada transaksi</p>
                                <p className="text-gray-400 text-sm mt-1">Transaksi akan muncul di sini</p>
                            </Card.Body>
                        </Card>
                    ) : (
                        <div className="space-y-4">
                            {transaction.map((t,i)=>(
                                (() => {
                                    const transactionKey = getTransactionKey(t, i)
                                    const statusMeta = getTransactionStatusMeta(t.transaction_approve_status_name)
                                    const detailList = getTransactionDetailList(t)
                                    const itemNames = getTransactionItemNames(t)
                                    const primaryDetail = detailList[0]
                                    const primaryItemName = primaryDetail ? getDetailItemName(primaryDetail) : itemNames[0]
                                    const remainingItemsCount = Math.max(0, itemNames.length - 1)
                                    const canExpand = detailList.length > 1 || remainingItemsCount > 0
                                    const isExpanded = Boolean(expandedTransactions[transactionKey])
                                    const transactionReference = getTransactionReference(t)
                                    const displayedTransactionId = transactionReference.transaction_id || transactionReference.transaction_number || '-'
                                    const isPendingStatus = normalizeStatus(t.transaction_approve_status_name) === 'pending'
                                    const shippingStatusName = getShippingStatusName(t)
                                    const canReceiveShipping = isShippingReceivable(t)
                                    const isReceivingShipping = receivingTransactionKey === transactionKey
                                    const primaryImage = primaryDetail ? getDetailItemImage(primaryDetail) : DefaultProduct
                                    const walletPaidAmount = getWalletBalanceAmount(t)
                                    const qrisPaidAmount = getQrisGatewayAmount(t)
                                    const hasWalletPayment = (walletPaidAmount || 0) > 0
                                    const hasQrisPayment = (qrisPaidAmount || 0) > 0
									const needsAfterPaymentSchedule = t?.delivery_schedule?.required_after_payment === true && !t?.delivery_schedule?.scheduled_at

                                    return (
                                        <Card
                                            key={transactionKey}
                                            className="cursor-pointer bg-white shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
                                            onClick={() => handleOpenTransactionDetail(t)}
                                        >
                                            <Card.Body className="p-4 space-y-3">
                                                <div className="flex items-start justify-between gap-2">
                                                    <div className="flex items-center gap-1 text-xs text-gray-500">
                                                        <AiOutlineCalendar className="text-sm" />
                                                        <span>{moment(t.transaction_created_datetime).format('DD MMM YYYY, HH:mm')}</span>
                                                    </div>
                                                    <div className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${statusMeta.className}`}>
                                                        {statusMeta.icon}
                                                        <span className="capitalize">{t.transaction_approve_status_name || 'Unknown'}</span>
                                                    </div>
                                                </div>

                                                <div className="flex items-start gap-3">
                                                    <div className="flex-shrink-0">
                                                        <img
                                                            src={primaryImage}
                                                            alt="Product"
                                                            className="w-14 h-14 rounded-lg object-cover shadow-sm"
                                                            onError={(event) => {
                                                                event.target.src = DefaultProduct
                                                            }}
                                                        />
                                                    </div>

                                                    <div className="min-w-0 flex-1 space-y-1">
                                                        <div className="flex items-center gap-1 text-xs text-gray-500">
                                                            <AiOutlineShoppingCart className="text-sm" />
                                                            <span className="font-medium text-gray-700">Produk</span>
                                                        </div>
                                                        <p className="text-sm font-semibold text-gray-800 leading-tight line-clamp-1">
                                                            {primaryItemName}
                                                        </p>

                                                        <div className="flex items-center gap-2">
                                                            <span className="font-mono text-xs text-gray-600">
                                                                ID {displayedTransactionId}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-0.5 text-[11px] text-gray-600 hover:bg-gray-50"
                                                                onClick={(event) => handleCopyTransactionId(event, displayedTransactionId)}
                                                            >
                                                                <AiOutlineCopy className="text-xs" />
                                                                <span>Salin</span>
                                                            </button>
                                                        </div>

                                                        {shippingStatusName && (
                                                            <div className="flex items-center justify-end text-right text-[11px]">
                                                                <span className="inline-flex max-w-full rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 font-semibold text-sky-700">
                                                                    <span className="truncate">{shippingStatusName}</span>
                                                                </span>
                                                            </div>
                                                        )}

                                                        {primaryDetail && detailList.length <= 1 && (
                                                            <div className="flex justify-end">
                                                                {renderReviewAction(t, primaryDetail)}
                                                            </div>
                                                        )}

                                                        {remainingItemsCount > 0 && (
                                                            <div className="flex items-center justify-between gap-2">
                                                                <span className="inline-flex rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                                                                    +{remainingItemsCount} produk lainnya
                                                                </span>
                                                                <button
                                                                    type="button"
                                                                    className="text-[11px] font-semibold text-blue-600 underline underline-offset-2 hover:text-blue-700"
                                                                    onClick={(event) => {
                                                                        event.stopPropagation()
                                                                        handleToggleExpanded(transactionKey)
                                                                    }}
                                                                >
                                                                    {isExpanded ? 'Sembunyikan detail' : 'Lihat detail'}
                                                                </button>
                                                            </div>
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="inline-flex text-[11px] font-semibold text-slate-600 underline underline-offset-2 hover:text-blue-700"
                                                            onClick={(event) => {
                                                                event.stopPropagation()
                                                                handleOpenTransactionDetail(t)
                                                            }}
                                                        >
                                                            Detail transaksi
                                                        </button>
                                                    </div>
                                                </div>

                                                {canExpand && isExpanded && (
                                                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                                                        <p className="mb-1 text-[11px] font-semibold text-gray-700">Rincian Produk</p>
                                                        {detailList.length > 0 ? (
                                                            <div className="space-y-2">
                                                                {detailList.map((detail, itemIndex) => (
                                                                    <div key={`${transactionKey}-item-${itemIndex}`} className="flex items-center justify-between gap-2 rounded-md bg-white px-2 py-2">
                                                                        <div className="min-w-0 flex items-center gap-2">
                                                                            <img
                                                                                src={getDetailItemImage(detail)}
                                                                                alt={getDetailItemName(detail)}
                                                                                className="h-9 w-9 flex-shrink-0 rounded-md object-cover"
                                                                                onError={(event) => {
                                                                                    event.target.src = DefaultProduct
                                                                                }}
                                                                            />
                                                                            <div className="min-w-0">
                                                                                <p className="line-clamp-1 text-xs font-semibold text-gray-800">
                                                                                    {getDetailItemName(detail)}
                                                                                </p>
                                                                                {getReviewReason(t, detail) && !getDetailCanReview(t, detail) && !getDetailAlreadyReviewed(detail) ? (
                                                                                    <p className="line-clamp-1 text-[10px] text-gray-500">
                                                                                        {getReviewReason(t, detail)}
                                                                                    </p>
                                                                                ) : null}
                                                                            </div>
                                                                        </div>
                                                                        <div className="flex-shrink-0">
                                                                            {renderReviewAction(t, detail)}
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <ul className="space-y-1 text-xs text-gray-700">
                                                                {itemNames.map((itemName, itemIndex) => (
                                                                    <li key={`${transactionKey}-item-${itemIndex}`} className="line-clamp-1">
                                                                        {itemIndex + 1}. {itemName}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        )}
                                                    </div>
                                                )}

                                                <div className="border-t border-gray-100 pt-3">
                                                    <div className="flex items-center justify-between">
                                                        <span className="text-xs font-medium text-gray-500">Total Belanja</span>
                                                        <span className="text-base font-bold text-gray-900">
                                                            Rp {Intl.NumberFormat('en-ID').format(toSafeNumber(t.transaction_total_amount, 0))}
                                                        </span>
                                                    </div>
                                                    {(hasWalletPayment || hasQrisPayment) && (
                                                        <div className="mt-2 space-y-1 border-t border-dashed border-gray-100 pt-2 text-xs">
                                                            {hasWalletPayment && (
                                                                <div className="flex items-center justify-between text-gray-600">
                                                                    <span>Dibayar melalui Saldo</span>
                                                                    <span className="font-medium">Rp {Intl.NumberFormat('en-ID').format(walletPaidAmount)}</span>
                                                                </div>
                                                            )}
                                                            {hasQrisPayment && (
                                                                <div className="flex items-center justify-between text-gray-600">
                                                                    <span>Dibayar melalui QRIS</span>
                                                                    <span className="font-medium">Rp {Intl.NumberFormat('en-ID').format(qrisPaidAmount)}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>

                                                {isPendingStatus && (
                                                    <div className="space-y-2">
                                                        <button
                                                            type="button"
                                                            className="w-full text-center text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700"
                                                            onClick={(event) => handlePaymentHelp(event, t)}
                                                        >
                                                            Memiliki masalah dalam pembayaran? klik disini
                                                        </button>
                                                        <Button
                                                            className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white border-none"
                                                            onClick={(event) => {
                                                                event.stopPropagation()
                                                                handleResume(t)
                                                            }}
                                                        >
                                                            <AiOutlineCreditCard className="mr-2" />
                                                            Lanjutkan Pembayaran
                                                        </Button>
                                                    </div>
                                                )}

										{needsAfterPaymentSchedule && (
											<Button className="w-full border-none bg-blue-600 text-white hover:bg-blue-700" onClick={(event) => {
												event.stopPropagation()
												setAfterPaymentSchedule({ transaction_id: transactionReference.transaction_id, schedule: t.delivery_schedule })
												setAfterPaymentScheduledAt(toLocalDatetimeInput(t.delivery_schedule.earliest_schedule_at))
											}}>Pilih Jadwal Pengiriman</Button>
										)}

                                                {canReceiveShipping && (
                                                    <Button
                                                        className="w-full border-none bg-green-600 text-white hover:bg-green-700 disabled:bg-green-300"
                                                        disabled={isReceivingShipping}
                                                        onClick={(event) => handleReceiveShipping(event, t, transactionKey)}
                                                    >
                                                        <AiOutlineCheckCircle className="mr-2" />
                                                        {isReceivingShipping ? 'Memproses...' : 'Pesanan Diterima'}
                                                    </Button>
                                                )}
                                            </Card.Body>
                                        </Card>
                                    )
                                })()
                            ))}
                        </div>
                    )}
                    {/* Load More */}
                    {transaction.length > 0 && hasMore && (
                        <div className="pt-2">
                            <Button
                                className="w-full bg-blue-600 hover:bg-blue-700 text-white border-none font-semibold"
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                            >
                                {loadingMore ? 'Memuat...' : 'Muat Lebih Banyak'}
                            </Button>
                        </div>
                    )}

                    {transaction.length > 0 && !hasMore && (
                        <p className="text-center text-xs text-gray-500 pb-2">
                            Semua transaksi sudah dimuat
                        </p>
                    )}
                </div>
            </div>

            {token && (
                <MidT
                    clientKey={paymentGateway?.public_configuration?.client_key || process.env.REACT_APP_CLIENT_KEY_MIDTRANS}
                    token={token}
                    onSuccess={(result)=>handlePaySuccess(result)}
                    onError={(result)=>handlePayError(result)}
                    onPending={(result)=>handlePayPending(result)}
                    onClose={()=>handlePayClose()}
                />
            )}

            {qrisPayment?.value ? (
                <QrisPaymentModal
                    payment={qrisPayment}
                    onFetchStatus={fetchQrisStatus}
                    onClose={closeQrisPayment}
                    onExpired={handleQrisExpired}
                    onSuccess={handleQrisSuccess}
                />
            ) : null}

			{afterPaymentSchedule && (
				<div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
					<div className="w-full max-w-md rounded-3xl bg-white p-6 shadow-xl">
						<h3 className="text-xl font-bold text-slate-900">Jadwalkan pengiriman</h3>
						<p className="mt-2 text-sm text-slate-600">Pembayaran telah diterima. Pilih waktu pengiriman saat toko buka untuk menyelesaikan pesanan.</p>
						<input type="datetime-local" className="input input-bordered mt-4 w-full" step="300" min={toLocalDatetimeInput(afterPaymentSchedule.schedule.earliest_schedule_at)} max={toLocalDatetimeInput(afterPaymentSchedule.schedule.latest_schedule_at)} value={afterPaymentScheduledAt} onChange={(event) => setAfterPaymentScheduledAt(event.target.value)} />
						<div className="mt-5 flex gap-3"><button className="btn flex-1 text-white" disabled={afterPaymentScheduleSaving} onClick={() => setAfterPaymentSchedule(null)}>Nanti</button><button className="btn flex-1 bg-blue-600 text-white" disabled={afterPaymentScheduleSaving} onClick={saveAfterPaymentSchedule}>{afterPaymentScheduleSaving ? 'Menyimpan...' : 'Simpan Jadwal'}</button></div>
					</div>
				</div>
			)}

            {reviewModal.open && (
                <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 px-4 pt-4 pb-[calc(4rem+max(1rem,env(safe-area-inset-bottom)))]">
                    <div className="max-h-[calc(100dvh-5rem)] w-full max-w-sm overflow-y-auto overscroll-contain rounded-2xl bg-white shadow-xl">
                        <form onSubmit={handleSubmitReview} className="space-y-4 p-4">
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex min-w-0 items-center gap-3">
                                    <img
                                        src={reviewProductImage}
                                        alt={reviewProductName}
                                        className="h-14 w-14 flex-shrink-0 rounded-lg object-cover"
                                        onError={(event) => {
                                            event.target.src = DefaultProduct
                                        }}
                                    />
                                    <div className="min-w-0">
                                        <p className="line-clamp-2 text-sm font-semibold text-gray-900">
                                            {reviewProductName}
                                        </p>
                                        <p className="mt-1 font-mono text-[11px] text-gray-500">
                                            {formatShortTransactionId(reviewTransactionReference.transaction_number)}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-lg leading-none text-gray-600 hover:bg-gray-200"
                                    onClick={closeReviewModal}
                                    disabled={reviewSubmitting}
                                    aria-label="Tutup modal review"
                                >
                                    <AiOutlineClose />
                                </button>
                            </div>

                            <div>
                                <label className="mb-2 block text-xs font-semibold text-gray-700">
                                    Rating
                                </label>
                                <div className="flex items-center gap-1">
                                    {[1, 2, 3, 4, 5].map((value) => {
                                        const active = value <= reviewRating
                                        return (
                                            <button
                                                key={value}
                                                type="button"
                                                className="flex h-10 w-10 items-center justify-center rounded-md text-2xl text-yellow-500 hover:bg-yellow-50"
                                                onClick={() => setReviewRating(value)}
                                                aria-label={`Rating ${value}`}
                                            >
                                                {active ? <AiFillStar /> : <AiOutlineStar />}
                                            </button>
                                        )
                                    })}
                                </div>
                            </div>

                            <div>
                                <label className="mb-2 block text-xs font-semibold text-gray-700">
                                    Komentar
                                </label>
                                <textarea
                                    className="min-h-[96px] w-full resize-none rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-800 outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100"
                                    value={reviewComment}
                                    onChange={(event) => setReviewComment(event.target.value)}
                                    placeholder="Bagikan pengalaman Anda"
                                    maxLength={1000}
                                    disabled={reviewSubmitting}
                                />
                            </div>

                            <div className="flex gap-2 pt-1">
                                <button
                                    type="button"
                                    className="h-10 flex-1 rounded-lg border border-gray-200 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                                    onClick={closeReviewModal}
                                    disabled={reviewSubmitting}
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    className="h-10 flex-1 rounded-lg bg-yellow-500 text-sm font-semibold text-white hover:bg-yellow-600 disabled:bg-gray-300 disabled:text-gray-500"
                                    disabled={reviewSubmitting || reviewRating < 1}
                                >
                                    {reviewSubmitting ? 'Mengirim...' : 'Kirim Review'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    )
}

export default TransactionList
